/**
 * Data table for VSC reports.
 */
import * as React from 'react';
import { ClipboardList } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DataTableFilters } from '@/components/common/data-table/DataTableFilters';
import { DataTable } from '@/components/common/data-table';
import { Button } from '@/components/forms/Button';
import { VscReportRow } from './VscReportRow';
import { VscReportCard } from './VscReportCard';
import { VscReportFiltersContent } from './VscReportFilters';
import { VscReportDetailsPanel } from '@/components/vsc-reports/renderVscReportDetails';
import { DeleteModal } from '@/components/common/modals/DeleteModal';
import { ValidateModal } from '@/components/common/modals/ValidateModal';
import { useDataTable } from '@/hooks/useDataTable';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';

import {
  getAllVscReports,
  getVscReport,
  validateVscReport,
  exportVscReportsExcel,
  deleteVscReport,
} from '@/services/vsc-reports.service';
import { usePageActions } from '@/hooks/usePageActions';
import { useConnectedUser } from '@/hooks/useConnectedUser';
import { cn } from '@/lib/utils';
import {
  formatVscDeleteModalItemName,
  formatVscReportCountLabel,
  getVscBulkDeleteDescription,
  getVscBulkDeleteTooltip,
  getVscBulkValidateDescription,
  getVscBulkValidateTooltip,
  getVscExpandedColSpan,
  getVscExportTooltip,
  hasVscReportActiveFilters,
  mergeVscReportPageActions,
  resolveVscTableRows,
  getVscBulkDeleteSuccessMessage,
  getVscBulkValidateSuccessMessage,
  toVscReportApiFilterParams,
  toVscReportListParams,
  canValidateVscReport,
  isVscReportSelectable,
  resolveElevatedValidatePermission,
  deriveBulkSelectionState,
} from './vsc-report-table-utils';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { useTableSelection } from '@/hooks/useTableSelection';
import { DataTableHeadCheckbox } from '@/components/common/data-table/DataTableRowCheckbox';
import {
  DEFAULT_VSC_REPORT_FILTERS,
  ALL_VSC_FILTER_GROUP_IDS,
  FILTER_GROUP_LABELS,
  type FilterGroupId,
  type VscReportPageActions,
} from './vsc-report-configs';
import type { ViewMode } from '@/types/data-table.types';
import type { ElementWidth } from '@/types/interface-elements.types';
import type { VscReport, VscReportFilters } from '@/types/vsc-report.types';

/** Props for {@link VscReportsDataTable}. */
interface VscReportsDataTableProps {
  vscReports?: VscReport[];
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  layoutWidth?: ElementWidth;
  expandedRowId?: string | null;
  onToggleRow?: (id: string) => void;
  onCloseDetails?: () => void;
  onEditReport?: (vscReportId: string) => void;
  enableFilters?: boolean;
  defaultFilters?: VscReportFilters;
  isSection?: boolean;
  pageActions?: VscReportPageActions;
}

/** Memoized filter strip so the table does not re-render the whole filter tree. */
const VscReportFiltersRenderer = React.memo<{
  filters: VscReportFilters;
  allowedFilterIds: string[];
  visibleFilterIds: string[];
  onToggleStatus: (status: string) => void;
  onSetDateRange: (range: VscReportFilters['dateRange']) => void;
  onSetCustomDateRange: (from: Date | undefined, to: Date | undefined) => void;
  onSetEstablishment?: (id: string | undefined) => void;
  onSetSearch?: (value: string | undefined) => void;
}>(
  ({
    filters,
    allowedFilterIds,
    visibleFilterIds,
    onToggleStatus,
    onSetDateRange,
    onSetCustomDateRange,
    onSetEstablishment,
    onSetSearch,
  }) => (
    <VscReportFiltersContent
      filters={filters}
      allowedFilterIds={allowedFilterIds}
      visibleFilterIds={visibleFilterIds}
      onToggleStatus={onToggleStatus}
      onSetDateRange={onSetDateRange}
      onSetCustomDateRange={onSetCustomDateRange}
      onSetEstablishment={onSetEstablishment}
      onSetSearch={onSetSearch}
    />
  ),
);

/** Data table for VSC reports. */
export const VscReportsDataTable: React.FC<VscReportsDataTableProps> = ({
  vscReports: externalVscReports,
  viewMode: externalViewMode,
  onViewModeChange: externalOnViewModeChange,
  layoutWidth = 'full',
  expandedRowId,
  onToggleRow,
  onCloseDetails,
  onEditReport,
  enableFilters = true,
  defaultFilters = DEFAULT_VSC_REPORT_FILTERS,
  isSection = false,
  pageActions,
}) => {
  const queryClient = useQueryClient();

  const rbac = usePageActions('vscReports');
  const { canValidate, canExportCsv } = rbac;
  const { isAdmin, isManager, isCoordinator } = useConnectedUser();
  const canValidateReport = resolveElevatedValidatePermission(canValidate, {
    isAdmin,
    isManager,
    isCoordinator,
  });

  /** Caller flags overlay hook RBAC; validate also requires an elevated role. */
  const effectivePageActions = React.useMemo(
    () =>
      mergeVscReportPageActions(pageActions, {
        canEditRow: rbac.canEditRow,
        canDeleteRow: rbac.canDeleteRow,
        canViewRow: rbac.canViewRow,
        canValidate: canValidateReport,
      }),
    [pageActions, rbac, canValidateReport],
  );

  const [presentationMode, setPresentationMode] = React.useState<'view' | 'edit'>('edit');

  /** Closing the panel resets to edit so the next expand is writable. */
  React.useEffect(() => {
    if (!expandedRowId) setPresentationMode('edit');
  }, [expandedRowId]);

  /** Expand in read-only mode (row-view). */
  const openAsView = React.useCallback(
    (id: string) => {
      setPresentationMode('view');
      onToggleRow?.(id);
    },
    [onToggleRow],
  );

  /** Expand in edit mode, or hand off to the dedicated edit view. */
  const openAsEdit = React.useCallback(
    (id: string) => {
      setPresentationMode('edit');
      onToggleRow?.(id);
    },
    [onToggleRow],
  );

  const handleRowView = React.useCallback(
    (vscReport: VscReport) => {
      if (!onToggleRow) return;
      openAsView(vscReport.id);
    },
    [onToggleRow, openAsView],
  );

  /** Skip `validated` rows; prefer the dedicated edit view when provided. */
  const handleRowEdit = React.useCallback(
    (vscReport: VscReport) => {
      if (vscReport.status === 'validated') return;
      if (onEditReport) {
        onCloseDetails?.();
        onEditReport(vscReport.id);
        return;
      }
      if (onToggleRow) {
        openAsEdit(vscReport.id);
      }
    },
    [onToggleRow, onCloseDetails, onEditReport, openAsEdit],
  );

  const [deleteId, setDeleteId] = React.useState<{ vscReportId: string } | null>(null);

  const [validateId, setValidateId] = React.useState<{ vscReportId: string } | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = React.useState(false);
  const [bulkValidateOpen, setBulkValidateOpen] = React.useState(false);

  const selection = useTableSelection();

  const filterGroupIds = ALL_VSC_FILTER_GROUP_IDS;
  const tableName = 'vsc-reports-management';

  const dt = useDataTable<VscReportFilters, FilterGroupId>({
    tableName,
    defaultFilters,
    allFilterGroupIds: filterGroupIds,
    layoutWidth,
    isSection,
    externalViewMode,
    onExternalViewModeChange: externalOnViewModeChange,
    externalExpandedRowId: expandedRowId,
    onCloseDetails,
  });

  const { data: apiResponse, isLoading } = useQuery({
    queryKey: ['vsc-reports', dt.currentPage, dt.itemsPerPage, dt.filters],
    queryFn: () =>
      getAllVscReports(toVscReportListParams(dt.filters, dt.currentPage, dt.itemsPerPage)),
    enabled: !externalVscReports,
  });

  const data = React.useMemo(
    () => resolveVscTableRows(externalVscReports, apiResponse?.data),
    [externalVscReports, apiResponse?.data],
  );

  /** Checkbox column when the user can delete or validate at least one row. */
  const showBulkSelection = effectivePageActions.canDeleteRow || canValidate || canValidateReport;

  const getReportCanValidate = React.useCallback(
    (vscReport: VscReport) => canValidateVscReport(vscReport, canValidateReport),
    [canValidate, canValidateReport],
  );

  const { selectableIds, selectedDeletableReports, selectedValidatableReports } = React.useMemo(
    () =>
      deriveBulkSelectionState(
        data,
        selection.selectedIds,
        canValidateReport,
        effectivePageActions,
      ),
    [data, selection.selectedIds, canValidateReport, effectivePageActions],
  );

  /** Drop ids that left the page or are no longer selectable. */
  const selectableIdsKey = selectableIds.join('\0');
  React.useEffect(() => {
    selection.pruneTo(selectableIds);
  }, [selectableIdsKey, selectableIds, selection.pruneTo]);

  const allSelectableSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selection.selectedIds.includes(id));
  const someSelectableSelected =
    selectableIds.some((id) => selection.selectedIds.includes(id)) && !allSelectableSelected;

  const totalItems = apiResponse?.total || data.length;
  const totalPages = Math.ceil(totalItems / dt.itemsPerPage) || 1;
  const startIndex = (dt.currentPage - 1) * dt.itemsPerPage;
  const endIndex = startIndex + dt.itemsPerPage;

  const [isExporting, setIsExporting] = React.useState(false);
  const isEmptyForExport = React.useMemo(
    () => (externalVscReports ? data.length === 0 : !isLoading && totalItems === 0),
    [externalVscReports, data.length, isLoading, totalItems],
  );

  /** Export the current filter set (not only the visible page). */
  const handleExportExcel = React.useCallback(async () => {
    if (isEmptyForExport) {
      toast.warning(getErrorMessage('exportEmpty'));
      return;
    }
    setIsExporting(true);
    try {
      await exportVscReportsExcel(toVscReportApiFilterParams(dt.filters));
      toast.success('Export Excel réussi !');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsExporting(false);
    }
  }, [dt.filters, isEmptyForExport]);

  const {
    data: expandedReportRaw,
    isLoading: isLoadingExpandedReport,
    isError: isExpandedReportError,
    error: expandedReportError,
  } = useQuery({
    queryKey: ['vsc-report', expandedRowId],
    queryFn: () => getVscReport(expandedRowId!),
    enabled: !!expandedRowId,
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError: isExpandedReportError,
    error: expandedReportError,
    catalogKey: 'vscReportNotFound',
    onClose: onCloseDetails,
    entityId: expandedRowId,
  });

  const hasActiveFilters = React.useMemo(
    () => hasVscReportActiveFilters(dt.filters, defaultFilters),
    [dt.filters, defaultFilters],
  );

  const deleteMutation = useMutation({
    mutationFn: (payload: { vscReportId: string }) => deleteVscReport(payload.vscReportId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['vsc-reports'], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ['vsc-report'], refetchType: 'all' }),
      ]);
      setDeleteId(null);
      toast.success('Rapport VSC supprimé avec succès');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const validateMutation = useMutation({
    mutationFn: async ({ vscReportId }: { vscReportId: string }) => validateVscReport(vscReportId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['vsc-reports'], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ['vsc-report'], refetchType: 'all' }),
      ]);
      setValidateId(null);
      toast.success('Rapport VSC validé avec succès');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (vscReportsToDelete: VscReport[]) => {
      await Promise.all(vscReportsToDelete.map((vscReport) => deleteVscReport(vscReport.id)));
    },
    onSuccess: async (_data, vscReportsToDelete) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['vsc-reports'], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ['vsc-report'], refetchType: 'all' }),
      ]);
      toast.success(getVscBulkDeleteSuccessMessage(vscReportsToDelete.length));
      selection.clear();
      setBulkDeleteOpen(false);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const bulkValidateMutation = useMutation({
    mutationFn: async (vscReportsToValidate: VscReport[]) => {
      await Promise.all(vscReportsToValidate.map((vscReport) => validateVscReport(vscReport.id)));
    },
    onSuccess: async (_data, vscReportsToValidate) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['vsc-reports'], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ['vsc-report'], refetchType: 'all' }),
      ]);
      toast.success(getVscBulkValidateSuccessMessage(vscReportsToValidate.length));
      selection.clear();
      setBulkValidateOpen(false);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const handleDeleteReport = React.useCallback((vscReport: VscReport) => {
    setDeleteId({ vscReportId: vscReport.id });
  }, []);

  const handleValidateReport = React.useCallback((vscReport: VscReport) => {
    setValidateId({ vscReportId: vscReport.id });
  }, []);

  /** Header checkbox: select every selectable id on the page, or clear. */
  const handleToggleSelectAll = React.useCallback(() => {
    if (allSelectableSelected) {
      selection.clear();
      return;
    }
    selection.selectAll(selectableIds);
  }, [allSelectableSelected, selectableIds, selection]);

  const handleBulkDeleteConfirm = React.useCallback(() => {
    if (selectedDeletableReports.length === 0) return;
    bulkDeleteMutation.mutate(selectedDeletableReports);
  }, [selectedDeletableReports, bulkDeleteMutation]);

  const handleBulkValidateConfirm = React.useCallback(() => {
    if (selectedValidatableReports.length === 0) return;
    bulkValidateMutation.mutate(selectedValidatableReports);
  }, [selectedValidatableReports, bulkValidateMutation]);

  const vscReportToDelete = React.useMemo(
    () => (deleteId ? data.find((vscReport) => vscReport.id === deleteId.vscReportId) : null),
    [deleteId, data],
  );

  /** Toggle one status chip and reset pagination. */
  const toggleStatusFilter = React.useCallback(
    (status: string) => {
      dt.setFilters((prev) => ({
        ...prev,
        statuses: prev.statuses.includes(status)
          ? prev.statuses.filter((s) => s !== status)
          : [...prev.statuses, status],
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setDateRange = React.useCallback(
    (range: VscReportFilters['dateRange']) => {
      dt.setFilters((prev) => ({ ...prev, dateRange: range }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  /** Custom from/to; any bound forces `dateRange` to `all`. */
  const setCustomDateRange = React.useCallback(
    (from: Date | undefined, to: Date | undefined) => {
      dt.setFilters((prev) => ({
        ...prev,
        customDateFrom: from,
        customDateTo: to,
        dateRange: from || to ? 'all' : prev.dateRange,
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setEstablishment = React.useCallback(
    (establishmentId: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, establishmentId }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setSearch = React.useCallback(
    (value: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, search: value }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const filtersContent = React.useMemo(
    () => (
      <VscReportFiltersRenderer
        filters={dt.filters}
        allowedFilterIds={dt.allowedFilterIds}
        visibleFilterIds={dt.visibleFilterIds}
        onToggleStatus={toggleStatusFilter}
        onSetDateRange={setDateRange}
        onSetCustomDateRange={setCustomDateRange}
        onSetEstablishment={setEstablishment}
        onSetSearch={setSearch}
      />
    ),
    [
      dt.filters,
      dt.allowedFilterIds,
      dt.visibleFilterIds,
      setSearch,
      toggleStatusFilter,
      setDateRange,
      setCustomDateRange,
      setEstablishment,
    ],
  );

  const filterGroups = React.useMemo(
    () => [
      {
        id: 'filters',
        label: 'Filtres',
        type: 'custom' as const,
        customRender: () => filtersContent,
      },
    ],
    [filtersContent],
  );

  const configureContent = React.useMemo(
    () => (
      <div className="flex flex-wrap gap-3">
        {filterGroupIds
          .filter((id) => dt.allowedFilterIds.includes(id))
          .map((id) => {
            const isOn = dt.filterVisibility[id] ?? true;
            return (
              <Button
                key={id}
                variant="custom"
                size="sm"
                onClick={() => dt.handleToggleFilterVisibility(id)}
                disabled={dt.isSavingFilterVisibility}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
                  dt.isSavingFilterVisibility && 'opacity-60 cursor-not-allowed',
                  isOn
                    ? 'bg-[var(--color-2-nuance-1)] border-[var(--color-2-nuance-1)] text-white'
                    : 'bg-transparent border-[color-mix(in_srgb,var(--color-1-nuance-3)_50%,transparent)] text-muted-foreground hover:border-[var(--color-1-nuance-2)]',
                )}
              >
                {FILTER_GROUP_LABELS[id]}
              </Button>
            );
          })}
      </div>
    ),
    [
      filterGroupIds,
      dt.filterVisibility,
      dt.allowedFilterIds,
      dt.isSavingFilterVisibility,
      dt.handleToggleFilterVisibility,
    ],
  );

  /** Prefer the single-report fetch (full payload) over the list row. */
  const expandedReport = expandedReportRaw ?? null;

  const itemLabelShort = 'rapport(s) VSC';

  const renderExpanded = React.useCallback(
    (vscReport: VscReport) => (
      <VscReportDetailsPanel
        vscReport={vscReport}
        presentationMode={presentationMode}
        onActionSuccess={presentationMode === 'edit' ? onCloseDetails : undefined}
      />
    ),
    [presentationMode, onCloseDetails],
  );

  return (
    <DataTable
      data={data}
      totalItems={totalItems}
      isLoading={isLoading}
      viewMode={dt.viewMode}
      onViewModeChange={dt.handleViewModeChange}
      currentPage={dt.currentPage}
      totalPages={totalPages}
      startIndex={startIndex}
      endIndex={endIndex}
      itemLabel={itemLabelShort}
      onPageChange={dt.setCurrentPage}
      hasActiveFilters={hasActiveFilters}
      containerRef={dt.containerRef}
      expandedColSpan={getVscExpandedColSpan(showBulkSelection)}
      toolbarActions={
        <>
          {showBulkSelection && (canValidate || canValidateReport) && (
            <Button
              preset="toolbar-validate"
              disabled={
                selectedValidatableReports.length === 0 ||
                bulkValidateMutation.isPending ||
                bulkDeleteMutation.isPending
              }
              onClick={() => setBulkValidateOpen(true)}
              tooltipText={getVscBulkValidateTooltip(selectedValidatableReports.length)}
            />
          )}
          {showBulkSelection && effectivePageActions.canDeleteRow && (
            <Button
              preset="toolbar-delete"
              disabled={
                selectedDeletableReports.length === 0 ||
                bulkDeleteMutation.isPending ||
                bulkValidateMutation.isPending
              }
              onClick={() => setBulkDeleteOpen(true)}
              tooltipText={getVscBulkDeleteTooltip(selectedDeletableReports.length)}
            />
          )}
          {enableFilters && (
            <Button
              preset="toolbar-filter"
              className={cn(dt.showFilters && 'section-action-button-active')}
              showBadge={hasActiveFilters}
              onClick={() => {
                dt.setShowFilters((prev) => {
                  if (!prev) dt.setShowConfigure(false);
                  return !prev;
                });
              }}
            />
          )}
          {canExportCsv && (
            <Button
              preset="toolbar-export"
              onClick={handleExportExcel}
              loading={isExporting}
              disabled={isEmptyForExport}
              tooltipText={getVscExportTooltip(isEmptyForExport, isExporting)}
            />
          )}
        </>
      }
      filtersPanel={
        enableFilters ? (
          <DataTableFilters
            show={dt.showFilters}
            hasActiveFilters={hasActiveFilters}
            filterGroups={filterGroups}
            onClearFilters={dt.clearFilters}
            resultCount={totalItems}
            resultLabel={itemLabelShort}
            onToggleConfigure={() => dt.setShowConfigure((prev) => !prev)}
            showConfigure={dt.showConfigure}
            configureContent={configureContent}
          />
        ) : undefined
      }
      renderTableHead={() => (
        <tr>
          {showBulkSelection && (
            <th className="w-10 text-center">
              <div className="flex items-center justify-center">
                <DataTableHeadCheckbox
                  checked={allSelectableSelected}
                  indeterminate={someSelectableSelected}
                  disabled={selectableIds.length === 0}
                  onCheckedChange={handleToggleSelectAll}
                />
              </div>
            </th>
          )}
          <th>Séance</th>
          <th>VSC</th>
          <th>Statut</th>
          <th>Date saisie</th>
          <th className="w-20 text-center">Actions</th>
        </tr>
      )}
      renderTableRow={(vscReport) => {
        const vscReportCanValidate = getReportCanValidate(vscReport);
        const selectable = isVscReportSelectable(
          vscReport,
          vscReportCanValidate,
          effectivePageActions,
        );
        return (
          <VscReportRow
            vscReport={vscReport}
            onView={onToggleRow ? () => handleRowView(vscReport) : undefined}
            onEdit={onEditReport || onToggleRow ? () => handleRowEdit(vscReport) : undefined}
            onDelete={
              effectivePageActions.canDeleteRow ? () => handleDeleteReport(vscReport) : undefined
            }
            onValidate={canValidateReport ? () => handleValidateReport(vscReport) : undefined}
            pageActions={effectivePageActions}
            isSelectable={showBulkSelection && selectable}
            isSelected={selection.isSelected(vscReport.id)}
            onToggleSelect={() => selection.toggle(vscReport.id)}
          />
        );
      }}
      expandedRowId={dt.expandedRowId}
      renderExpandedRow={(vscReport) => renderExpanded(vscReport)}
      renderCard={(vscReport) => {
        const vscReportCanValidate = getReportCanValidate(vscReport);
        const selectable = isVscReportSelectable(
          vscReport,
          vscReportCanValidate,
          effectivePageActions,
        );
        return (
          <VscReportCard
            vscReport={vscReport}
            onView={onToggleRow ? () => handleRowView(vscReport) : undefined}
            onEdit={onEditReport || onToggleRow ? () => handleRowEdit(vscReport) : undefined}
            onDelete={
              effectivePageActions.canDeleteRow ? () => handleDeleteReport(vscReport) : undefined
            }
            onValidate={canValidateReport ? () => handleValidateReport(vscReport) : undefined}
            pageActions={effectivePageActions}
            isSelectable={showBulkSelection && selectable}
            isSelected={selection.isSelected(vscReport.id)}
            onToggleSelect={() => selection.toggle(vscReport.id)}
          />
        );
      }}
      emptyIcon={ClipboardList}
      emptyText="Aucun rapport VSC trouvé"
      title="Rapports VSC"
      sidebarMenuId="menu-vsc-reports"
      isSection={isSection}
      expandedItem={expandedReport}
      isExpandedDetailLoading={isLoadingExpandedReport}
      onCloseDetails={onCloseDetails}
      renderDetailsContent={(vscReport) => renderExpanded(vscReport)}
      modals={
        <>
          <DeleteModal
            open={!!deleteId}
            onClose={() => setDeleteId(null)}
            onConfirm={() => {
              if (deleteId) deleteMutation.mutate(deleteId);
            }}
            itemName={formatVscDeleteModalItemName(vscReportToDelete)}
            isLoading={deleteMutation.isPending}
          />
          <ValidateModal
            open={!!validateId}
            onClose={() => setValidateId(null)}
            onConfirm={() => {
              if (validateId) void validateMutation.mutateAsync(validateId);
            }}
            itemName="rapport VSC"
            isLoading={validateMutation.isPending}
          />
          <DeleteModal
            open={bulkDeleteOpen}
            onClose={() => setBulkDeleteOpen(false)}
            onConfirm={handleBulkDeleteConfirm}
            itemName={formatVscReportCountLabel(selectedDeletableReports.length)}
            description={getVscBulkDeleteDescription(selectedDeletableReports.length)}
            isLoading={bulkDeleteMutation.isPending}
          />
          <ValidateModal
            open={bulkValidateOpen}
            onClose={() => setBulkValidateOpen(false)}
            onConfirm={handleBulkValidateConfirm}
            itemName={formatVscReportCountLabel(selectedValidatableReports.length)}
            description={getVscBulkValidateDescription(selectedValidatableReports.length)}
            isLoading={bulkValidateMutation.isPending}
          />
        </>
      }
    />
  );
};
